import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "./api";
import { useAuth } from "./auth-context";
import { loadRazorpay } from "./razorpay";
import { SUBSCRIPTION_PLANS } from "./subscription-plans";

const SubscriptionContext = createContext(null);

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState({});
  const [trialUsed, setTrialUsed] = useState(false);
  const [loading, setLoading] = useState(false);

  const refreshSubscription = useCallback(async () => {
    const { data } = await api.get("/protected/subscription");

    // No subscription at all → auto-activate trial (handles race on login
    // and existing users who signed up before subscription system was added).
    if (!data.subscription && !data.trialUsed) {
      try {
        const { data: trialData } = await api.post("/protected/subscription/activate", {});
        setSubscription(trialData.subscription ?? null);
        setUsage(trialData.usage ?? {});
        setTrialUsed(true);
        return trialData;
      } catch {
        // 409 = trial already used (subscription expired) — fall through and show null
      }
    }

    setSubscription(data.subscription ?? null);
    setUsage(data.usage ?? {});
    setTrialUsed(data.trialUsed ?? false);
    return data;
  }, []);

  // For paid plans: creates a Razorpay order and opens checkout, then verifies.
  // For free Trial: calls /activate directly (no payment needed).
  const subscribe = useCallback(
    (planId, prefill = {}) =>
      new Promise((resolve, reject) => {
        (async () => {
          const plan = SUBSCRIPTION_PLANS[planId];

          if (plan?.isFree) {
            try {
              const { data } = await api.post("/protected/subscription/activate", { planId });
              setSubscription(data.subscription);
              setUsage(data.usage ?? {});
              resolve(data);
            } catch (err) {
              reject(err);
            }
            return;
          }

          let order;
          try {
            const { data } = await api.post("/protected/subscription/create-order", { planId });
            order = data;
          } catch (err) {
            reject(err);
            return;
          }

          const scriptLoaded = await loadRazorpay();
          if (!scriptLoaded) {
            reject(new Error("Couldn't load the payment checkout. Check your connection and try again."));
            return;
          }

          const rzp = new window.Razorpay({
            key: order.key,
            amount: order.amount,
            currency: order.currency,
            order_id: order.orderId,
            name: "EasyLister",
            description: `${plan?.name} Plan · ₹${plan?.price}/month`,
            prefill: { name: prefill.name, email: prefill.email },
            theme: { color: "#7c3aed" },
            handler: async (response) => {
              try {
                const { data } = await api.post("/protected/subscription/verify-payment", {
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  planId,
                });
                setSubscription(data.subscription);
                setUsage(data.usage ?? {});
                resolve(data);
              } catch (err) {
                reject(err);
              }
            },
            modal: {
              ondismiss: () => reject(new Error("Payment cancelled")),
            },
          });

          rzp.on("payment.failed", (resp) => {
            reject(new Error(resp?.error?.description || "Payment failed"));
          });

          rzp.open();
        })();
      }),
    [],
  );

  const canUse = useCallback(
    (feature) => {
      if (!subscription) return false;
      const plan = SUBSCRIPTION_PLANS[subscription.plan];
      if (!plan) return false;
      const limit = plan.limits[feature];
      if (limit === undefined || limit === 0) return false;
      if (limit === Infinity) return true;
      return (usage[feature] ?? 0) < limit;
    },
    [subscription, usage],
  );

  const getLimit = useCallback(
    (feature) => SUBSCRIPTION_PLANS[subscription?.plan]?.limits[feature] ?? 0,
    [subscription],
  );

  const getUsage = useCallback((feature) => usage[feature] ?? 0, [usage]);

  useEffect(() => {
    if (!user) {
      setSubscription(null);
      setUsage({});
      setTrialUsed(false);
      return;
    }
    setLoading(true);
    refreshSubscription()
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user, refreshSubscription]);

  return (
    <SubscriptionContext.Provider
      value={{ subscription, usage, trialUsed, loading, refreshSubscription, subscribe, canUse, getLimit, getUsage }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error("useSubscription must be used within SubscriptionProvider");
  return ctx;
};
